import { stateAbbreviations } from '../constants/locations';
import { getCrimeDisplayName } from '../constants/crimes';
import { parseKrimePathname } from './urlRouting';
import { buildExportPageTitle } from './exportData';

const DEFAULT_TITLE = 'KRIME | Crime Data & Analysis';

/** Marketing / landing title — matches `public/index.html` `<title>` and default OG tags. */
export const DEFAULT_SITE_TITLE = 'KRIME | Understand the true crime around you';

/** Short label in document title after "KRIME " (before the em dash). Singular. */
const ANALYSIS_TAB_TITLE = {
  reports: 'Report',
  totals: 'Total',
  trends: 'Trend',
  rates: 'Rate',
  scores: 'Score',
};

function getAnalysisTabTitleSegment(tab) {
  return ANALYSIS_TAB_TITLE[tab] || ANALYSIS_TAB_TITLE.reports;
}

/**
 * End of title: "... Crime Data" vs "... Crime Charts" (after crime label).
 * Reports tab has no chart view → always "Data". Map is not called out in the title → treat like Data.
 */
function getCrimeDataOrChartsSuffix(tab, view) {
  if (tab === 'reports') return 'Data';
  if (view === 'chart') return 'Charts';
  return 'Data';
}

/**
 * Browser title: aligned with export filenames when possible (see buildExportPageTitle).
 * Falls back to legacy "… {year} … Data|Charts" when location data is incomplete.
 */
export function buildPageTitle(locationName, {
  crime, year, source, tab, view,
  isHistorical = false,
  locationData = null,
  dataViewMode = 'locationName',
} = {}) {
  const displayName = abbreviateState(locationName);
  if (!displayName) return DEFAULT_TITLE;

  if (locationData?.info) {
    return buildExportPageTitle({
      view: dataViewMode,
      activeTab: tab,
      isHistorical,
      selectedCrime: crime || 'all_crime',
      selectedDateRange: year != null && year !== '' ? String(year) : '2024',
      locationData,
      locationName,
    });
  }

  if (isHistorical) {
    const tabSeg = getAnalysisTabTitleSegment(tab);
    const crimeLabelForHist =
      crime === 'all_crime' && tab !== 'scores'
        ? 'Crime'
        : getCrimeDisplayName(crime || 'all_crime', source || 'FBI_CIUS');
    return `KRIME ${tabSeg} - ${displayName} Historical ${crimeLabelForHist} Data`;
  }

  const tabSeg = getAnalysisTabTitleSegment(tab);
  const crimeLabel = getCrimeDisplayName(
    crime || 'all_crime',
    source || 'FBI_CIUS'
  );
  const y = year != null && year !== '' ? String(year) : '2024';
  const endWord = getCrimeDataOrChartsSuffix(tab, view);

  return `KRIME ${tabSeg} - ${displayName} ${y} ${crimeLabel} ${endWord}`;
}

/**
 * For cities and counties the internal name is "Name, Full State Name".
 * Returns the name with the state abbreviated, e.g. "Cleveland, OH" or
 * "Cuyahoga County, OH". Non-city/county names are returned unchanged.
 */
function abbreviateState(locationName) {
  if (!locationName) return locationName;
  const commaIdx = locationName.lastIndexOf(',');
  if (commaIdx === -1) return locationName;
  const namePart = locationName.slice(0, commaIdx).trim();
  const statePart = locationName.slice(commaIdx + 1).trim();
  const abbr = stateAbbreviations[statePart];
  return abbr ? `${namePart}, ${abbr}` : locationName;
}
const DEFAULT_DESC =
  'Explore crime statistics for cities, counties, metros, and states across the US. View recent and historical totals, trends, rates, and scores.';

function setMeta(attrName, attrValue, contentValue) {
  let el = document.querySelector(`meta[${attrName}="${attrValue}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', contentValue);
}

/**
 * Updates document.title and all OG / Twitter Card meta tags.
 * Pass app state so the title matches the URL (tab, view, year, crime, source).
 */
export function updatePageMeta(locationName, appState = {}) {
  const displayName = abbreviateState(locationName);

  const title = buildPageTitle(locationName, appState);

  const description = displayName
    ? `Explore crime statistics for ${displayName}. View recent and historical totals, trends, rates, and scores.`
    : DEFAULT_DESC;

  const url = window.location.href;
  const image = `${window.location.origin}/logo_navy_gold_silver.png`;

  document.title = title;

  try {
    sessionStorage.setItem(
      `krime:title:${window.location.pathname}${window.location.search}`,
      title
    );
  } catch {
    /* ignore quota / private mode */
  }

  // Standard
  setMeta('name', 'description', description);

  // Open Graph
  setMeta('property', 'og:type',        'website');
  setMeta('property', 'og:site_name',   'KRIME');
  setMeta('property', 'og:title',       title);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:url',         url);
  setMeta('property', 'og:image',       image);

  // Twitter Card
  setMeta('name', 'twitter:card',        'summary_large_image');
  setMeta('name', 'twitter:title',       title);
  setMeta('name', 'twitter:description', description);
  setMeta('name', 'twitter:image',       image);
}

/**
 * Restores the public site title and OG/Twitter meta (logged-out landing, after logout).
 * Clears the per-URL sessionStorage title cache so a hard refresh does not restore a Krime tab title.
 */
export function resetPageMetaToDefault() {
  const url = window.location.href;
  const image = `${window.location.origin}/logo_navy_gold_silver.png`;

  document.title = DEFAULT_SITE_TITLE;

  try {
    sessionStorage.removeItem(
      `krime:title:${window.location.pathname}${window.location.search}`
    );
  } catch {
    /* ignore */
  }

  setMeta('name', 'description', DEFAULT_DESC);
  setMeta('property', 'og:type', 'website');
  setMeta('property', 'og:site_name', 'KRIME');
  setMeta('property', 'og:title', DEFAULT_SITE_TITLE);
  setMeta('property', 'og:description', DEFAULT_DESC);
  setMeta('property', 'og:url', url);
  setMeta('property', 'og:image', image);
  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', DEFAULT_SITE_TITLE);
  setMeta('name', 'twitter:description', DEFAULT_DESC);
  setMeta('name', 'twitter:image', image);
}

/**
 * Builds a per-platform share title and body text.
 * Parses crime_type and date_range from shareUrl so the body always
 * reflects the exact search being shared.
 * Format: "{date_range} {crime_type} for {locationName}: {url}"
 */
export function buildShareText(locationName, shareUrl) {
  const displayName = abbreviateState(locationName);

  let title = displayName ? `KRIME - ${displayName}` : DEFAULT_TITLE;
  let body;
  if (displayName) {
    try {
      const url = new URL(shareUrl);
      const parsed = parseKrimePathname(url.pathname, url.search);
      title = buildPageTitle(locationName, {
        crime: parsed.crime,
        year: parsed.year,
        source: parsed.source,
        tab: parsed.tab,
        view: parsed.view,
        isHistorical: parsed.historical,
        locationData: null,
      });
      const crimeType = parsed.crime || 'all_crime';
      const dateRange = parsed.year || '2024';
      const crimeLabel = getCrimeDisplayName(
        crimeType,
        parsed.source || 'FBI_CIUS'
      ).toLowerCase();
      body = `${dateRange} ${crimeLabel} data for ${displayName}`;
    } catch {
      body = `KRIME data for ${displayName}`;
    }
  } else {
    body = 'Explore crime data and analysis on KRIME';
  }

  return { title, body };
}
